import { useAnalytics } from "@codat/analytics";
import { Typography, useToast } from "@codat/orchard-ui";
import { DataConnection, usePatchConnectionMutation } from "api/public";
import React from "react";
import getTryCatchErrorMessage from "utils/getTryCatchErrorMessage";

import Modal from "components/Modal";

import { SourceType } from "../../pages/Companies/Companies";

interface UnlinkConfirmationModalProps {
  companyId: string;
  connection: DataConnection;
  onCancel: () => void;
  onSuccess: (companyId: string, sourceType: SourceType) => void;
}

const UnlinkConfirmationModal: React.FC<UnlinkConfirmationModalProps> = ({
  companyId,
  connection,
  onCancel,
  onSuccess,
}) => {
  const [{ loading }, unlinkConnection] = usePatchConnectionMutation({
    companyId,
    connectionId: connection.id,
    status: "Unlinked",
  });
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const onUnlinkClick = () => {
    sendEvent("Unlink Button - Click", {
      companyId,
      connectionId: connection.id,
    });
    unlinkConnection()
      .then(() => {
        addToast({
          message: `Unlinked connection to ${connection.platformName}`,
          variant: "success",
        });
        onSuccess(companyId, connection.sourceType as SourceType);
      })
      .catch((e) =>
        addToast({
          message: `Failed to unlink connection: ${getTryCatchErrorMessage(e)}`,
          variant: "error",
        })
      );
  };

  return (
    <Modal
      isPinned
      onClose={onCancel}
      eventProperties={{ companyId, connectionId: connection.id }}
      size="small"
      data-testid="unlink-connection-modal"
    >
      <>
        <Modal.Header
          title={`Unlink ${connection.platformName}?`}
          onClose={null}
        />
        <Modal.Body variant="light">
          <Modal.Section variant="fill">
            <Typography variant="body_md" as="p" noMargin>
              You&apos;ll be able to access all the data already synced, but
              you&apos;ll need to re-link the connection to update them.
            </Typography>
          </Modal.Section>
        </Modal.Body>
        <Modal.Footer
          variant="light"
          buttons={[
            {
              key: "cancelButton",
              label: "Cancel",
              onClick: () => {
                sendEvent("Cancel Button - Click", {
                  companyId,
                  connectionId: connection.id,
                });
                onCancel();
              },
              "data-testid": "unlink-modal-cancel",
              variant: "secondary",
              color: "interface",
            },
            {
              key: "unlinkButton",
              label: "Unlink",
              onClick: onUnlinkClick,
              "data-testid": "unlink-modal-submit",
              isLoading: loading,
              color: "negative",
            },
          ]}
        />
      </>
    </Modal>
  );
};

export default UnlinkConfirmationModal;
